import { useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { SECURITY_SUBJECT_STATUSES, type SecurityAccountView } from '@ni/shared';
import { useApi, useOrg } from '../api/app-context';
import { useSubjects } from '../api/hooks';
import { errorLine } from '../api/errors';
import { IfWrites } from '../components/write-access';
import { PeopleTable } from '../components/people/people-table';
import { Modal } from '../components/ui/modal';
import { LinkRow } from '../components/ui/row-link';
import { orgHasProtectionAccounts } from '../lib/vertical';
import { AccountChoice, useSecurityAccounts } from '../components/accounts/account-choice';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { Button } from '../components/ui/button';
import { Field } from '../components/ui/field';
import { RecordTable } from '../components/record/record-table';
import { TopBar } from '../components/ui/top-bar';
import { useEvents } from '../api/event-hooks';
import { formatDay } from '../lib/format';
import { EventsTable, flattenEvents } from './events';
import './accounts.css';

/* The account picker lives in components/accounts; re-exported for the pages
   that already import it from here. */
export { AccountChoice, useSecurityAccounts };

/**
 * **Protection accounts** — the security vertical's one page of its own.
 *
 * An account is the engagement a firm is on: a principal, the people and
 * organizations around them, and the context the office wrote about why the
 * engagement exists. Everything else about a security org is the standard stack
 * narrowed by `account_id` — the People list, the Organizations list, the
 * knowledge base and the record pages are the ones every other vertical uses.
 * What is left here is the account itself: naming it, writing its context,
 * archiving it, and a way in to the records on it.
 *
 * The page is reachable only in a security org. Elsewhere it redirects to Home
 * rather than rendering a list of accounts that cannot exist — the route is
 * mounted for every vertical so that a pasted link answers rather than reading
 * as Not found while the reader is signed in to the wrong org.
 */

/** Every read of the org's accounts — the invalidation prefix the three writes drop. */
export function invalidateAccounts(cache: QueryClient, orgId: string): Promise<void> {
  return cache.invalidateQueries({ queryKey: ['security', orgId] });
}

/**
 * **Which account a create door files under**, given what was asked for and
 * which accounts the firm actually has open.
 *
 * A rule rather than a line inside the hook, because the suite has no press and
 * no second render: what is decided here is only reachable through a select
 * nobody can click and an address nobody can change, so it is asserted as a
 * function (`accounts.spec.tsx`) the way `writeHeld` and `peopleParams` are.
 *
 * **Nothing is trusted because it was on the address.** `?account_id=` is a
 * link somebody pasted: it can name an archived engagement, another firm's, or
 * a typo. An id that is not among the *active* options is treated as if it had
 * not been given at all, so the ordinary rules decide instead — the sole
 * account if there is one, and otherwise nothing, which holds the create. The
 * alternative is the failure this is written against: an id the select cannot
 * show, an enabled Create, and a 400 at submit.
 *
 * **A pick answers the address it was made against.** `pickedAgainst` is the
 * preset in force when the office chose, so a link that pushes a different
 * `?account_id=` into a form already on screen — the account page's two Add
 * links, on a second engagement — is a new question rather than one the old
 * answer still stands for. Holding the preset in state instead would have made
 * the address readable once, at mount, and ignored every later one.
 */
export function accountForNewRecord(
  asked: { preset: string | null; picked: string; pickedAgainst: string | null },
  options: readonly { id: string }[],
): string {
  const offered = (id: string | null): boolean =>
    id !== null && id !== '' && options.some((one) => one.id === id);
  const picked = (asked.pickedAgainst ?? '') === (asked.preset ?? '') ? asked.picked : '';
  if (offered(picked)) return picked;
  if (offered(asked.preset)) return asked.preset as string;
  return options.length === 1 ? options[0]!.id : '';
}

/**
 * **The account a create door files its record under** (security fold,
 * 2026-09-22), and the control that asks for it.
 *
 * The three cases the two doors share:
 *
 * - **one active account** — nothing is asked. A menu with one item is a
 *   question with one answer, and the sole account is what the create sends.
 * - **several** — the office chooses, and the create is held until they have.
 *   There is no default: "whichever was created first" would file a principal
 *   under somebody else's engagement.
 * - **none** — the select is still drawn, with the line that says why it is
 *   empty and where to go. A form that cannot be submitted and does not say so
 *   is the worse failure.
 *
 * `preset` is `?account_id=` on the address, which the account page's two Add
 * links carry. It is read on every render rather than captured at mount, and it
 * is believed only where it names an account the firm has open — see
 * {@link accountForNewRecord}, which is where both of those rules live.
 *
 * In every other vertical this is off: `missing` is false, there is no control,
 * and the create sends no account — which is what the server requires there.
 */
export function useAccountForNewRecord(preset: string | null): {
  accountId: string;
  missing: boolean;
  control: ReactNode;
} {
  const org = useOrg();
  const accounts = useSecurityAccounts('active');
  const [pick, setPick] = useState<{ id: string; against: string | null }>({
    id: '',
    against: preset,
  });
  if (!orgHasProtectionAccounts(org)) {
    return { accountId: '', missing: false, control: null };
  }
  const options = accounts.data ?? [];
  const accountId = accountForNewRecord(
    { preset, picked: pick.id, pickedAgainst: pick.against },
    options,
  );
  /* One account is no choice at all, however the address spells it. The select
     is drawn for every other case, the empty one included — that is where the
     line below says why it has nothing in it. */
  const control =
    options.length === 1 ? null : (
      <>
        <AccountChoice
          active
          value={accountId}
          onChange={(id) => setPick({ id, against: preset })}
        />
        {accounts.isSuccess && options.length === 0 ? (
          <div className="field-note">
            No active protection accounts.{' '}
            <Link className="link" to="/accounts?create=1">
              Create one
            </Link>{' '}
            before entering a record.
          </div>
        ) : null}
      </>
    );
  return { accountId, missing: !accountId, control };
}

export function ErrorMessage({ error }: { error: unknown }) {
  return error ? <p role="alert">{errorLine(error)}</p> : null;
}

export function NewSecurityAccount({
  onCreated,
  onCancel,
}: {
  onCreated: (account: SecurityAccountView) => void;
  onCancel?: () => void;
}) {
  const api = useApi();
  const org = useOrg();
  const cache = useQueryClient();
  const [label, setLabel] = useState('');
  const [context, setContext] = useState('');
  const create = useMutation({
    mutationFn: () => api.createSecurityAccount({ label, engagement_context: context }),
    onSuccess: async (result) => {
      await invalidateAccounts(cache, org.id);
      onCreated(result.account);
    },
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label="Account name" htmlFor="account-new-name">
        <input
          id="account-new-name"
          type="text"
          required
          maxLength={200}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label="Engagement context (optional)" htmlFor="account-new-context">
        <textarea
          id="account-new-context"
          rows={6}
          maxLength={20000}
          value={context}
          onChange={(e) => setContext(e.target.value)}
        />
        <div className="field-note">
          Describe the protection assignment. This context guides research; it is not evidence.
        </div>
      </Field>
      <div className="modal-actions">
        <button className="btn go" disabled={create.isPending || !label.trim()}>
          {create.isPending ? 'Creating…' : 'Create account'}
        </button>
        {onCancel && (
          <Button variant="act" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <ErrorMessage error={create.error} />
    </form>
  );
}

/** The list of engagements, searchable and filtered by state. */
export function Accounts() {
  const api = useApi();
  const org = useOrg();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'active' | 'archived' | 'all'>('active');
  const [offset, setOffset] = useState(0);
  const [params, setParams] = useSearchParams();
  const creating = params.get('create') === '1';
  const list = useQuery({
    queryKey: ['security', org.id, 'accounts', state, offset, ...(q ? [q] : [])],
    queryFn: () => api.securityAccounts(state, offset, q),
    enabled: orgHasProtectionAccounts(org),
  });
  if (!orgHasProtectionAccounts(org)) return <Navigate to="/" replace />;

  const accounts = list.data?.accounts ?? [];

  return (
    <div className="column accounts">
      <div className="head">
        <div className="titles">
          <div className="title">Protection accounts</div>
          <div className="subline">
            Organize records around a principal and the people or organizations relevant to their
            protection.
          </div>
        </div>
        <div className="actions-right">
          <IfWrites>
            <button className="btn hero" onClick={() => setParams({ create: '1' })}>
              New account
            </button>
          </IfWrites>
        </div>
      </div>

      <div className="account-tools" role="search">
        <input
          className="account-search"
          type="search"
          maxLength={200}
          autoComplete="off"
          placeholder="Search accounts"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
        />
        <label className="account-pick">
          <span className="account-pick-label">Show</span>
          <select
            className="control"
            value={state}
            onChange={(e) => {
              setState(e.target.value as typeof state);
              setOffset(0);
            }}
          >
            <option value="active">Active accounts</option>
            <option value="archived">Archived accounts</option>
            <option value="all">All accounts</option>
          </select>
        </label>
      </div>

      <ErrorMessage error={list.error} />
      <div className="account-list">
        {list.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : list.data?.total === 0 ? (
          <EmptyLine>
            {q || state !== 'active' ? 'No accounts match.' : 'No protection accounts yet.'}
          </EmptyLine>
        ) : accounts.length > 0 ? (
          <AccountsTable accounts={accounts} />
        ) : null}
      </div>
      {list.data && list.data.total > 50 && (
        <div className="list-foot account-pagination">
          <Button
            variant="act"
            tiny
            disabled={!offset}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Previous
          </Button>
          <Button
            variant="act"
            tiny
            disabled={!list.data || offset + 50 >= list.data.total}
            onClick={() => setOffset(offset + 50)}
          >
            Next
          </Button>
        </div>
      )}

      {creating && (
        <IfWrites>
          <Modal label="New protection account" onClose={() => setParams({})}>
            <div className="modal-head">New protection account</div>
            <div className="account-form">
              <NewSecurityAccount
                onCreated={(a) => navigate(`/accounts/${a.id}`)}
                onCancel={() => setParams({})}
              />
            </div>
          </Modal>
        </IfWrites>
      )}
    </div>
  );
}

/** Each engagement on a row: its name, what it is, when it last changed, its state. */
function AccountsTable({ accounts }: { accounts: readonly SecurityAccountView[] }) {
  return (
    <RecordTable
      headings={['Account', 'Engagement', 'Updated', 'State']}
      className="accounts-table"
    >
      {accounts.map((a) => (
        <LinkRow key={a.id} to={`/accounts/${a.id}`}>
          <td className="name">
            <Link className="account-name" to={`/accounts/${a.id}`}>
              {a.label}
            </Link>
          </td>
          <td className="quiet-cell context">
            <div className="clamp-2" title={a.engagement_context}>
              {a.engagement_context}
            </div>
          </td>
          <td className="quiet-cell updated">{formatDay(a.updated_at)}</td>
          <td className="quiet-cell state">{a.state === 'archived' ? 'Archived' : 'Active'}</td>
        </LinkRow>
      ))}
    </RecordTable>
  );
}

/** Keyed on org and account together: switching either discards the page's state. */
export function AccountPage() {
  const { accountId = '' } = useParams();
  const org = useOrg();
  if (!orgHasProtectionAccounts(org)) return <Navigate to="/" replace />;
  return <AccountContent key={`${org.id}-${accountId}`} accountId={accountId} />;
}

/** The roster's two halves, read as the People list reads them. */
const PRINCIPAL_STATUSES = ['principal'] as const;
const AROUND_STATUSES = SECURITY_SUBJECT_STATUSES.filter((status) => status !== 'principal');

/**
 * One engagement: what it is, and the records filed under it.
 *
 * The roster is the People list narrowed to this account rather than a roster of
 * its own, split by status into the principals and the people around them:
 * `useSubjects({ account_id, status })` is the same read `/people` makes, so the
 * two can never disagree about who is on an account, and the link under it opens
 * the full list with the chip already set. Events are the Events list's read,
 * narrowed the same way.
 */
function AccountContent({ accountId }: { accountId: string }) {
  const api = useApi();
  const org = useOrg();
  const [managing, setManaging] = useState(false);
  const account = useQuery({
    queryKey: ['security', org.id, accountId, 'account'],
    queryFn: () => api.securityAccount(accountId),
  });
  const principals = useSubjects({ account_id: accountId, status: PRINCIPAL_STATUSES, limit: 10 });
  const people = useSubjects({ account_id: accountId, status: AROUND_STATUSES, limit: 10 });
  const events = useEvents({ account_id: accountId, limit: 10 });
  const counts = account.data?.records;
  const view = account.data?.account;
  const active = view?.state === 'active';

  return (
    <div className="column account-page">
      <TopBar
        parent={{ to: '/accounts', label: 'Protection accounts' }}
        here={view?.label}
        actions={
          view ? (
            <IfWrites>
              <Button variant="act" onClick={() => setManaging(true)}>
                Manage account
              </Button>
            </IfWrites>
          ) : null
        }
      />

      <div className="head">
        <div className="titles">
          <div className="title">{view?.label ?? 'Protection account'}</div>
          {view && counts ? (
            <div className="subline account-facts">
              {active ? 'Active' : 'Archived'} · {counts.people}{' '}
              {counts.people === 1 ? 'person' : 'people'} · {counts.organizations}{' '}
              {counts.organizations === 1 ? 'organization' : 'organizations'}
            </div>
          ) : null}
        </div>
      </div>

      <ErrorMessage error={account.error ?? principals.error ?? people.error} />

      {view && (
        <>
          {view.state === 'archived' && (
            <p className="account-notice">Archived account. Existing records remain available.</p>
          )}
          {view.engagement_context ? (
            <p className="account-context">{view.engagement_context}</p>
          ) : (
            <p className="account-context none">No engagement context supplied.</p>
          )}

          {managing && (
            <IfWrites>
              <Modal label="Manage account" onClose={() => setManaging(false)}>
                <div className="modal-head">Manage account</div>
                <div className="account-form">
                  <AccountEditor
                    key={`${accountId}-${view.version}`}
                    account={view}
                    onClose={() => setManaging(false)}
                  />
                </div>
              </Modal>
            </IfWrites>
          )}

          <AccountBlock title="Principals">
            <Roster read={principals} empty="No principal on this account yet." />
          </AccountBlock>

          <AccountBlock
            title="People"
            actions={
              active ? (
                <IfWrites>
                  <Link className="btn act tiny" to={`/people/new?account_id=${accountId}`}>
                    Add person
                  </Link>
                  <Link className="btn act tiny" to={`/organizations/new?account_id=${accountId}`}>
                    Add organization
                  </Link>
                </IfWrites>
              ) : null
            }
          >
            <Roster read={people} empty="No one else on this account yet." />
            <div className="list-foot">
              <Link className="link" to={`/people?account_id=${accountId}`}>
                See all people on this account
              </Link>
              <Link className="link" to={`/organizations?account_id=${accountId}`}>
                See the organizations on this account
              </Link>
            </div>
          </AccountBlock>

          <AccountBlock title="Events">
            {events.isPending ? (
              <EmptyLine>
                <Loading />
              </EmptyLine>
            ) : events.isError ? (
              <ErrorMessage error={events.error} />
            ) : flattenEvents(events.data.pages).length === 0 ? (
              <EmptyLine>No events on this account yet.</EmptyLine>
            ) : (
              <>
                <EventsTable entries={flattenEvents(events.data.pages)} account={false} />
                <div className="list-foot">
                  <Link className="link" to={`/events?account_id=${accountId}`}>
                    See all events on this account
                  </Link>
                </div>
              </>
            )}
          </AccountBlock>
        </>
      )}
    </div>
  );
}

/** A block on the account page: its head on an ink rule, actions beside it. */
function AccountBlock({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="account-block">
      <div className="account-block-head">
        <h2>{title}</h2>
        {actions ? <div className="account-block-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function Roster({ read, empty }: { read: ReturnType<typeof useSubjects>; empty: string }) {
  /* A failed read is said once, in the error line under the head. */
  if (read.isError && !read.data) return null;
  if (read.isPending || !read.data) {
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  }
  if (read.data.entries.length === 0) return <EmptyLine>{empty}</EmptyLine>;
  return <PeopleTable entries={read.data.entries} />;
}

function AccountEditor({
  account,
  onClose,
}: {
  account: SecurityAccountView;
  onClose: () => void;
}) {
  const api = useApi();
  const org = useOrg();
  const cache = useQueryClient();
  const [label, setLabel] = useState(account.label);
  const [context, setContext] = useState(account.engagement_context);
  const save = useMutation({
    mutationFn: (state?: 'active' | 'archived') =>
      api.updateSecurityAccount(
        account.id,
        state
          ? { expected_version: account.version, state }
          : { expected_version: account.version, label, engagement_context: context },
      ),
    onSuccess: () => invalidateAccounts(cache, org.id),
  });

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined);
        }}
      >
        <Field label="Account name" htmlFor="account-edit-name">
          <input
            id="account-edit-name"
            type="text"
            disabled={account.state === 'archived'}
            required
            maxLength={200}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
        <Field label="Engagement context" htmlFor="account-edit-context">
          <textarea
            id="account-edit-context"
            rows={6}
            disabled={account.state === 'archived'}
            maxLength={20000}
            value={context}
            onChange={(e) => setContext(e.target.value)}
          />
          <div className="field-note">
            Changes apply to future runs. Existing runs retain the context they started with.
          </div>
        </Field>
        <div className="modal-actions">
          <button className="btn go" disabled={save.isPending || account.state === 'archived'}>
            Save context
          </button>
          <Button
            variant="act"
            disabled={save.isPending}
            onClick={() => save.mutate(account.state === 'active' ? 'archived' : 'active')}
          >
            {account.state === 'active' ? 'Archive account' : 'Restore account'}
          </Button>
          <Button variant="act" onClick={onClose}>
            Close
          </Button>
        </div>
        <p className="field-note">
          {account.state === 'active'
            ? 'Archiving stops new records and account edits. Existing records remain readable, and the account can be restored.'
            : 'Restore this account to add people, update context, and start research again.'}
        </p>
        <ErrorMessage error={save.error} />
      </form>
    </>
  );
}
